import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CreditCardForm from "../CreditCardForm/CreditCardForm";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  checkoutGroupBasket,
  fetchMyShareContribution,
  fetchRemainderContribution,
} from "../../api/groupBasketApi";
import {
  RAIL,
  cardDetailsComplete,
  cardDetailsFromForm,
  chargeRequestFor,
  clearGroupPayment,
  contributionPayOptions,
  hostCheckoutPayOptions,
  rememberGroupPayment,
  startGroupCharge,
} from "../../utils/groupBasketPayment";
import { groupBasketPath } from "../../utils/groupBasketLink";
import { accountPhone } from "../../utils/deliveryPhone";
import { validatePaymentPhone } from "../../utils/paymentPhone";
import { MARKET } from "../../config/market";
import { coverText, formatBasketMoney } from "../../utils/groupBasketView";
import GroupBasketCountdown from "./GroupBasketCountdown";
import {
  Choice,
  ChoiceGrid,
  FieldLabel,
  IconCircle,
  MUTED,
  Notice,
  QuietButton,
  PrimaryButton,
  SheetBody,
  SheetFoot,
  SheetHead,
  TEXT,
  TextInput,
  sheetPaperProps,
} from "./groupBasketStyles";

export const PAY_PURPOSE = Object.freeze({
  SHARE: "share",
  REMAINDER: "remainder",
  CHECKOUT: "checkout",
});

const TITLES = {
  [PAY_PURPOSE.SHARE]: "Pay your share",
  [PAY_PURPOSE.REMAINDER]: "Pay what is left",
  [PAY_PURPOSE.CHECKOUT]: "Check out for everyone",
};

const fetchContribution = {
  [PAY_PURPOSE.SHARE]: fetchMyShareContribution,
  [PAY_PURPOSE.REMAINDER]: fetchRemainderContribution,
};

const emailOf = (user) => user?.result?.email || user?.email || undefined;
const userIdOf = (user) => user?.result?._id || user?._id || null;

/*
 * Pay a share, the host's remainder, or the host's one order, on the
 * existing rails (utils/groupBasketPayment). The amount on screen and the
 * amount charged are the server's: the contribution's `amount`, or for
 * HOST_PAYS_ALL the created order's `totalPrice`.
 */
const GroupPaySheet = ({ open, purpose, basket, receivedAt, user, onClose, onBasket, onStarted }) => {
  const dispatch = useDispatch();
  const fullScreen = useMediaQuery("(max-width: 600px)");
  const [contribution, setContribution] = useState(null);
  const [loadState, setLoadState] = useState("idle");
  const [loadError, setLoadError] = useState("");
  const [methodKey, setMethodKey] = useState(null);
  const [mobile, setMobile] = useState(() => accountPhone(user) || "");
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const basketId = basket?.id;
  const currency = basket?.currency;
  const money = (amount) => formatBasketMoney(amount, currency);
  const checkout = purpose === PAY_PURPOSE.CHECKOUT;

  // A share or the remainder is paid against its own order, from the server.
  useEffect(() => {
    if (!open || checkout || !basketId) return undefined;
    let active = true;
    setLoadState("loading");
    setLoadError("");
    fetchContribution[purpose](basketId)
      .then(({ contribution: answer }) => {
        if (!active) return;
        setContribution(answer);
        setLoadState(answer ? "ready" : "failed");
      })
      .catch((err) => {
        if (!active) return;
        setLoadError(err.message);
        setLoadState("failed");
      });
    return () => {
      active = false;
    };
  }, [open, checkout, purpose, basketId]);

  useEffect(() => {
    if (!open) {
      setError("");
      setBusy(false);
      setContribution(null);
      setMethodKey(null);
    }
  }, [open]);

  const options = useMemo(
    () => (checkout ? hostCheckoutPayOptions() : contributionPayOptions(contribution?.allowedMethods)),
    [checkout, contribution]
  );
  const option = options.find((entry) => entry.method === methodKey) || null;

  const amountShown = checkout ? basket?.lock?.total : contribution?.amount;
  const settledAlready = !checkout && contribution && ["PAID", "PENDING"].includes(contribution.status);
  const phone = option?.needs === "phone" ? validatePaymentPhone(mobile, option.key) : null;
  const suggestedOption = options.find((entry) => entry.key === phone?.suggestedKey);
  const phoneOk = phone?.ok === true;
  const cardDetails = card ? cardDetailsFromForm(card) : null;
  const ready =
    option &&
    !busy &&
    !settledAlready &&
    (option.needs === "phone" ? phoneOk : option.needs === "card" ? cardDetailsComplete(cardDetails) : true) &&
    (checkout || loadState === "ready");

  const returnPath = groupBasketPath(basketId);

  const charge = async ({ orderId, amount, clientId, role }) => {
    const request = chargeRequestFor({
      option,
      orderId,
      amount,
      clientId,
      mobile,
      cardDetails,
      returnUrl: `${window.location.origin}${returnPath}`,
      email: emailOf(user),
    });
    if (!request.ok) return { ok: false, message: request.message };
    rememberGroupPayment({
      basketId,
      orderId,
      clientId,
      rail: request.rail,
      role,
      analyticsRail: option.analyticsRail,
    });
    const result = await startGroupCharge({ dispatch, request, returnPath });
    if (!result.ok) clearGroupPayment();
    return result;
  };

  const pay = async () => {
    if (!ready) return;
    setBusy(true);
    setError("");
    try {
      let result;
      if (checkout) {
        const { order, basket: placed } = await checkoutGroupBasket(basketId, option.method);
        if (placed) onBasket(placed);
        if (option.rail === RAIL.COD) {
          onStarted({ purpose, rail: RAIL.COD });
          onClose();
          return;
        }
        result = await charge({
          orderId: order?.orderId,
          amount: order,
          clientId: userIdOf(user),
          role: purpose,
        });
      } else {
        result = await charge({
          orderId: contribution.orderId,
          amount: contribution.amount,
          clientId: contribution.clientId,
          role: purpose,
        });
      }
      if (!result.ok) {
        if (!result.toasted) setError(result.message);
        setBusy(false);
        return;
      }
      if (!result.redirecting) {
        onStarted({ purpose, rail: option.rail });
        onClose();
      }
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const buttonLabel = busy
    ? "One moment"
    : checkout
      ? option?.rail === RAIL.COD
        ? "Place order, pay on delivery"
        : "Place order and pay"
      : amountShown != null
        ? `Pay ${money(amountShown)}`
        : "Pay";

  return (
    <Dialog
      open={Boolean(open)}
      onClose={busy ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={fullScreen ? undefined : sheetPaperProps}
    >
      <SheetHead>
        <div>
          <h2>{TITLES[purpose]}</h2>
          <p>{basket?.vendor?.name}</p>
        </div>
        <IconCircle type="button" onClick={onClose} aria-label="Close" disabled={busy}>
          <CloseIcon />
        </IconCircle>
      </SheetHead>
      <SheetBody>
        {!checkout && loadState === "loading" && <Muted>Getting your amount</Muted>}
        {!checkout && loadState === "failed" && (
          <Notice $tone="danger">{loadError || "Your amount could not be loaded. Close this and try again."}</Notice>
        )}

        {amountShown != null && (checkout || loadState === "ready") && (
          <AmountBlock>
            <span>{checkout ? "Order total" : purpose === PAY_PURPOSE.REMAINDER ? "Left to pay" : "Your share"}</span>
            <strong>{money(amountShown)}</strong>
            {!checkout && contribution?.payBy && (
              <GroupBasketCountdown
                deadline={{ at: contribution.payBy }}
                serverNow={basket?.deadlines?.serverNow}
                receivedAt={receivedAt}
                tone="wait"
                label="Pay within"
              />
            )}
          </AmountBlock>
        )}

        {purpose === PAY_PURPOSE.REMAINDER && basket?.remainder?.covers?.length > 0 && (
          <Muted>
            Covers {basket.remainder.covers.map(coverText(money)).join(", ")}.
          </Muted>
        )}

        {settledAlready ? (
          <Notice $tone={contribution.status === "PAID" ? "done" : "wait"}>
            {contribution.status === "PAID"
              ? "This is paid. Nothing more to do."
              : "Confirming your payment. Do not pay again."}
          </Notice>
        ) : (
          (checkout || loadState === "ready") && (
            <>
              <div>
                <FieldLabel as="span">Pay with</FieldLabel>
                <ChoiceGrid $columns={2}>
                  {options.map((entry) => (
                    <PayChoice
                      key={entry.method}
                      type="button"
                      $selected={entry.method === methodKey}
                      aria-pressed={entry.method === methodKey}
                      disabled={busy}
                      onClick={() => { setMethodKey(entry.method); setError(""); }}
                    >
                      {entry.imageUrl ? <Logo src={entry.imageUrl} alt="" /> : null}
                      <strong>{entry.label}</strong>
                      <small>{entry.method === methodKey ? "Selected" : entry.needs === "phone" ? "Pay from your phone" : entry.needs === "card" ? "Pay securely by card" : "Pay when it arrives"}</small>
                    </PayChoice>
                  ))}
                </ChoiceGrid>
              </div>

              {option?.needs === "phone" && (
                <div>
                  <FieldLabel htmlFor="group-pay-mobile">{option.label} number</FieldLabel>
                  <TextInput
                    id="group-pay-mobile"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    disabled={busy}
                    aria-invalid={Boolean(mobile && !phoneOk)}
                    aria-describedby="group-pay-phone-hint group-pay-phone-feedback"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder={`${MARKET.momoPrefixes?.[option.key]?.[0] || "0XX"} XXX XXXX`}
                  />
                  <Hint id="group-pay-phone-hint">Use the {option.label} number you want to pay from. It can differ from the rider’s contact number. Enter a local number or +{MARKET.dialCode}.</Hint>
                  <PhoneFeedback id="group-pay-phone-feedback" aria-live="polite" $invalid={Boolean(mobile && !phoneOk)}>
                    {mobile && phone?.message ? phone.message : phoneOk ? `The payment prompt will go to ${phone.local}. Approve it on that phone.` : "Enter your mobile money number to continue."}
                  </PhoneFeedback>
                  {suggestedOption && (
                    <QuietButton type="button" disabled={busy} onClick={() => { setMethodKey(suggestedOption.method); setError(""); }}>
                      Use {suggestedOption.label}
                    </QuietButton>
                  )}
                </div>
              )}

              {option?.needs === "card" && <CreditCardForm onFormChange={setCard} />}

              {option?.rail === RAIL.COD && (
                <Notice>You pay the rider in cash when the order arrives.</Notice>
              )}
            </>
          )
        )}

        {error ? <Notice $tone="danger" role="alert">{error}</Notice> : null}
      </SheetBody>
      {!settledAlready && (
        <SheetFoot>
          <PrimaryButton type="button" onClick={pay} disabled={!ready}>
            <LockOutlinedIcon aria-hidden="true" />
            {buttonLabel}
          </PrimaryButton>
        </SheetFoot>
      )}
    </Dialog>
  );
};

const Muted = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${MUTED};
`;

const AmountBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 16px;
  border-radius: 16px;
  background: #f3f7ef;

  span {
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${MUTED};
  }

  strong {
    font-size: 30px;
    font-weight: 800;
    color: ${TEXT};
    font-variant-numeric: tabular-nums;
  }
`;

const Logo = styled.img`
  height: 56px;
  width: 112px;
  max-width: 100%;
  object-fit: contain;
`;

const PayChoice = styled(Choice)`
  min-height: 132px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;

  small { font-size: 12px; font-weight: 500; }
`;

const PhoneFeedback = styled.p`
  margin: 8px 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ $invalid }) => $invalid ? "#a12622" : MUTED};
`;

const Hint = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${MUTED};
`;

export default GroupPaySheet;
